/**
 * Maintenance report grid — site alarm and maintenance log for a customer or plant.
 *
 * Server-backed via EntityGrid over odl-app/maintenance-report/. Columns come from
 * the backend grid-spec descriptor. A "Show" single-select and an alarm-category
 * multi-select drive filter[...] params via extraParams. Checkbox row selection
 * is enabled. The toolbar's CSV export writes the on-screen columns for every
 * site the active filters match, not just the loaded page.
 *
 * Contains:
 * - MaintenanceReportGrid — EntityGrid over odl-app/maintenance-report/ (default export)
 */

import { CheckBoxOutlineBlankTwoTone, CheckBoxTwoTone } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { isValid, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';

import { fetchAlarmCounts } from '@sophocles/api/alarms';
import { useUser } from '@sophocles/app';
import { EntityGrid } from '@sophocles/data-grids';
import AlarmChipCell from '@sophocles/data-grids/components/cells/AlarmChipCell';
import { DataGridMultiSelectFilter, DataGridSingleSelectFilter } from '@sophocles/data-grids/components/filters';
import useColumnVisibilityModel from '@sophocles/data-grids/datasource/useColumnVisibilityModel';
import { selectAlarmCategories, selectAlarmDefinitions } from '@sophocles/features/reference';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import { formatDateTime } from '@sophocles/utils/formatUtils';
import { buildActiveAlarmChips } from '@sophocles/views/reports/maintenanceReportAlarms';
import { buildMaintenanceReportParams } from '@sophocles/views/reports/maintenanceReportParams';

function MaintenanceReportGrid({ customerId, siteGroupId }) {
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const { user, isStaff } = useUser();
  const relayLoadTerm = useRelayLoadTerm();

  const [searchParams] = useSearchParams();
  const alarmCategories = useSelector(selectAlarmCategories);
  const alarmDefinitions = useSelector(selectAlarmDefinitions);

  // active_alarms rows carry only alarm display-name strings; index the alarm
  // catalog by that same field so each chip can resolve its severity color.
  const alarmDefByName = useMemo(
    () => new Map(alarmDefinitions.map(def => [def.display_name, def])),
    [alarmDefinitions],
  );

  const columnVisibility = useColumnVisibilityModel('maintenanceReportGrid');

  const translateAlarmLabel = useCallback(
    (def, name) => (def ? tAlarm(def.locale_key, { defaultValue: def.display_name || name }) : name),
    [tAlarm],
  );

  const [alarmCounts, setAlarmCounts] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filterOption, setFilterOption] = useState('default');

  // Capture initial alarm filter from URL query param (?alarms=offline or ?alarms=offline,bulb_outage)
  const [initialAlarmFilters] = useState(() => {
    const alarmsParam = searchParams.get('alarms');
    return alarmsParam
      ? alarmsParam
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      : [];
  });

  // Fetch per-category counts scoped to the plant.
  useEffect(() => {
    const params = siteGroupId ? { site_groups: siteGroupId } : {};
    fetchAlarmCounts(params).then(counts => setAlarmCounts(counts || {}));
  }, [siteGroupId]);

  // Build visible category options: non-admin-only (unless staff), count > 0, sorted by display_order.
  const categoryOptions = useMemo(() => {
    const options = [];
    alarmCategories.forEach(category => {
      if (!isStaff && category.visible_to_admins_only) return;
      const count = alarmCounts[category.id] || 0;
      if (count <= 0) return;
      options.push({
        id: category.id,
        shortName: category.key,
        displayOrder: category.display_order,
        label: tAlarm(category.locale_key, { defaultValue: category.display_name }),
        count,
      });
    });
    options.sort((a, b) => a.displayOrder - b.displayOrder);
    return options;
  }, [alarmCategories, alarmCounts, isStaff, tAlarm]);

  // Preselect alarm categories from URL query params once options are ready.
  const [appliedInitialAlarms, setAppliedInitialAlarms] = useState(false);
  useEffect(() => {
    if (appliedInitialAlarms || initialAlarmFilters.length === 0 || categoryOptions.length === 0) return;
    const matches = categoryOptions.filter(opt =>
      initialAlarmFilters.some(f => opt.shortName === f || String(opt.id) === f),
    );
    if (matches.length > 0) {
      setSelectedCategories(matches);
      setFilterOption('alarms');
      setAppliedInitialAlarms(true);
    }
  }, [initialAlarmFilters, categoryOptions, appliedInitialAlarms]);

  // extraParams drives the EntityGrid list fetch. Each key becomes filter[key] on the wire.
  const extraParams = useMemo(
    () => buildMaintenanceReportParams({ customerId, siteGroupId, filterOption, selectedCategories }),
    [customerId, siteGroupId, filterOption, selectedCategories],
  );

  // The descriptor labels the loads column with a static key; the file keeps
  // the tenant's own word for a relay load, as this report's CSV always has.
  const exportOptions = useMemo(
    () => ({
      fileName: 'maintenance-report.csv',
      columns: { loads: { headerName: relayLoadTerm.plural } },
    }),
    [relayLoadTerm],
  );

  // Render overrides on descriptor columns — sizing/order/headers come from the descriptor.
  const fieldOverrides = useMemo(
    () => ({
      latest_combined_timestamp: {
        renderCell: ({ value }) => {
          if (value == null) return '-';
          const parsed = parseISO(value);
          return isValid(parsed) ? formatDateTime(parsed, user) : t('general.na', 'N/A');
        },
      },
      maintenance_timestamp: {
        renderCell: ({ value }) => {
          if (value == null) return '-';
          const parsed = parseISO(value);
          return isValid(parsed) ? formatDateTime(parsed, user) : t('general.na', 'N/A');
        },
      },
      latest_alarm_timestamp: {
        renderCell: ({ value }) => {
          if (value == null) return '-';
          const parsed = parseISO(value);
          return isValid(parsed) ? formatDateTime(parsed, user) : t('general.na', 'N/A');
        },
      },
      group_owner__site_group__name: {
        renderCell: ({ row, value }) => {
          if (value == null) return t('general.unknown');
          return (
            <Link to={`/c/${customerId}/plant/${row.group_owner__site_group__id}/structures`}>
              {value}
              {row.group_owner__site_group__description ? ` - ${row.group_owner__site_group__description}` : ''}
            </Link>
          );
        },
      },
      name: {
        renderCell: ({ row, value }) => {
          if (value == null) return t('general.unknown');
          const link =
            siteGroupId != null
              ? `/c/${customerId}/plant/${siteGroupId}/structure/${row.id}`
              : `/c/${customerId}/plant/${row.group_owner__site_group__id}/structure/${row.id}`;
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
              <Box
                component={Link}
                to={link}
                sx={{ fontWeight: 'bold', color: 'primary.main', textDecoration: 'none' }}
              >
                {value}
              </Box>
              {row.description && (
                <Typography variant="caption" color="text.secondary">
                  {row.description}
                </Typography>
              )}
            </Box>
          );
        },
      },
      location_description: {
        renderCell: ({ value }) =>
          value ? (
            <Typography variant="caption" color="text.secondary">
              {value}
            </Typography>
          ) : null,
      },
      loads: {
        valueFormatter: value => {
          if (value == null || value === '') return '-';
          return Array.isArray(value) ? value.join(', ') : value;
        },
      },
      current_maintenance: {
        valueFormatter: value => {
          if (value == null) return '';
          return Array.isArray(value) ? value.join(', ') : value;
        },
      },
      active_alarms: {
        renderCell: ({ value }) => (
          <AlarmChipCell alarms={buildActiveAlarmChips(value, alarmDefByName, translateAlarmLabel)} />
        ),
      },
      comm_status: {
        renderCell: ({ value }) => {
          if (value == null) return t('general.unknown');
          return value ? (
            <CheckBoxTwoTone style={{ color: 'green' }} />
          ) : (
            <CheckBoxOutlineBlankTwoTone style={{ color: 'red' }} />
          );
        },
      },
    }),
    [customerId, siteGroupId, user, alarmDefByName, translateAlarmLabel, t],
  );

  const showOptions = useMemo(
    () => [
      { value: 'alarms', label: t('reports.showWithAlarms') },
      { value: 'maintenance', label: t('reports.showWithMaintenance') },
      { value: 'alarmsAndMaintenance', label: t('reports.showWithAlarmsAndMaintenance') },
    ],
    [t],
  );

  const categoryFilterOptions = useMemo(
    () => categoryOptions.map(opt => ({ value: opt.id, label: opt.label })),
    [categoryOptions],
  );

  const selectedCategoryIds = useMemo(() => selectedCategories.map(c => c.id), [selectedCategories]);

  const handleCategoryIdsChange = useCallback(
    nextIds => {
      const idSet = new Set(nextIds);
      setSelectedCategories(categoryOptions.filter(opt => idSet.has(opt.id)));
    },
    [categoryOptions],
  );

  const toolbarContent = (
    <>
      <DataGridSingleSelectFilter
        label={t('reports.show', 'Show')}
        value={filterOption === 'default' ? null : filterOption}
        onChange={value => setFilterOption(value ?? 'default')}
        allLabel={t('reports.showAll')}
        options={showOptions}
      />
      <DataGridMultiSelectFilter
        label={t('general.alarms')}
        values={selectedCategoryIds}
        onChange={handleCategoryIdsChange}
        options={categoryFilterOptions}
      />
    </>
  );

  return (
    <EntityGrid
      basePath="maintenance-report"
      fieldOverrides={fieldOverrides}
      extraParams={extraParams}
      options={{
        infiniteScroll: true,
        persistKey: 'maintenanceReportGrid',
        toolbarContent,
        export: exportOptions,
        toolbarVariant: 'flat',
        showSearch: false,
        selection: { enabled: true },
        columnVisibility,
        countNoun: t('reports.sites', 'sites'),
        gridProps: {
          density: 'compact',
          disableRowSelectionOnClick: false,
        },
      }}
    />
  );
}

export default MaintenanceReportGrid;
